// FI-22 · Entrega 2 (Hacer corte): lógica pura del corte guiado, separada de ShiftClosePage.
// Aquí solo se decide QUÉ decir (cuadra / faltan / sobran, el avance de la barra, la línea del turno
// encogido); las cifras vienen de calculateExpectedCash y del backend, nunca se recalculan aquí.
import { formatMoney } from './costFormatting';

export type CashTone = 'none' | 'ok' | 'over' | 'short';

const asMoney = (value: number) => formatMoney(value.toFixed(2));

/** Tono de la diferencia contado − esperado: sin contar, cuadra, sobrante o faltante. */
export function cashDifferenceTone(difference: number | null): CashTone {
  if (difference === null) return 'none';
  if (Math.abs(difference) < 0.005) return 'ok';
  return difference > 0 ? 'over' : 'short';
}

/** «cuadra ✓» · «sobran $15.00» · «faltan $23.00» · '' mientras no se ha contado. */
export function cashDifferenceLabel(difference: number | null): string {
  const tone = cashDifferenceTone(difference);
  if (tone === 'ok') return 'cuadra ✓';
  if (tone === 'over') return `sobran ${asMoney(difference as number)}`;
  if (tone === 'short') return `faltan ${asMoney(-(difference as number))}`;
  return '';
}

/** Lo que dice el eco grande junto al efectivo contado: el esperado en gris hasta que se cuenta. */
export function countedCashEcho(expectedCash: number, difference: number | null): string {
  return difference === null ? `esperado ${asMoney(expectedCash)}` : cashDifferenceLabel(difference);
}

/** Barra «Efectivo en caja»: esperado contra contado, como «Ticket hasta ahora» en el asistente. */
export function cashProgress(expectedCash: number, countedCash: string): { label: string; percent: number; tone: CashTone } {
  if (countedCash === '') return { label: `Esperado ${asMoney(expectedCash)} · aún sin contar`, percent: 0, tone: 'none' };
  const counted = Number(countedCash);
  const difference = Math.round((counted - expectedCash + Number.EPSILON) * 100) / 100;
  const percent = expectedCash <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((counted / expectedCash) * 100)));
  return { label: `Contado ${asMoney(counted)} de ${asMoney(expectedCash)} · ${cashDifferenceLabel(difference)}`, percent, tone: cashDifferenceTone(difference) };
}

// Hora de pared LITERAL de la tienda ('YYYY-MM-DDTHH:mm'), formateada sin reinterpretarla en la zona
// del dispositivo (mismo truco que WallDateTimeField: se parsea y formatea como UTC).
const DAY_FMT = new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const TIME_FMT = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
const cap = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const wallDate = (wall: string) => new Date(`${wall}:00Z`);

/** Rango del turno en una línea: «Sáb, 6 sept, 9:00 a. m. – 9:12 p. m.» (día repetido solo si cambia). */
export function shiftRangeLine(startAt: string, endAt: string): string {
  const start = wallDate(startAt);
  const end = wallDate(endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return `${startAt} – ${endAt}`;
  const sameDay = startAt.slice(0, 10) === endAt.slice(0, 10);
  const endText = sameDay ? TIME_FMT.format(end) : `${cap(DAY_FMT.format(end))}, ${TIME_FMT.format(end)}`;
  return `${cap(DAY_FMT.format(start))}, ${TIME_FMT.format(start)} – ${endText}`;
}

/** La fila encogida del bloque «Turno»: tienda · responsable · rango. Omite lo que no haya. */
export function shiftSummaryLine(input: { storeName: string | null; employeeName: string; startAt: string; endAt: string }): string {
  return [input.storeName, input.employeeName, shiftRangeLine(input.startAt, input.endAt)].filter((part) => Boolean(part)).join(' · ');
}
